import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { tickets as ticketsApi } from '../services/api';
import { comments as commentsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AlertModal from './AlertModal';
import ImproveDescriptionPreview from './ImproveDescriptionPreview';

/**
 * TicketModal Component
 * Uses .btn-modal-primary (gray) for modal buttons, .btn-secondary, .btn-success, .btn-danger for actions
 */
const TicketModal = ({ ticket, onClose, onUpdate }) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [improving, setImproving] = useState(false);
  const [error, setError] = useState('');
  const [alertModal, setAlertModal] = useState({ isOpen: false, type: 'info', message: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null, confirmText: 'Confirm' });

  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);

  // Improve description state
  const [showPreview, setShowPreview] = useState(false);
  const [improvedDescription, setImprovedDescription] = useState('');

  // Edit description state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(ticket.description || '');
  const [savingDescription, setSavingDescription] = useState(false);

  // Plan mode state
  const [activeTab, setActiveTab] = useState('description');
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);

  useEffect(() => {
    loadComments();
    loadPlan();
  }, [ticket.id]);

  const loadPlan = async () => {
    try {
      setLoadingPlan(true);
      const response = await ticketsApi.getPlan(ticket.id);
      setPlan(response.data.plan);
    } catch (err) {
      console.error('Failed to load plan:', err);
    } finally {
      setLoadingPlan(false);
    }
  };

  const loadComments = async () => {
    try {
      setLoadingComments(true);
      const response = await commentsApi.getForTicket(ticket.id);
      setComments(response.data);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmittingComment(true);
      const response = await commentsApi.create(ticket.id, newComment);
      setComments([...comments, response.data.comment]);
      setNewComment('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleSubmitJob = () => {
    if (ticket.job) {
      setAlertModal({
        isOpen: true,
        type: 'warning',
        message: 'This ticket already has a job associated with it.'
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      message: 'Submit a Thor Hammer job for this ticket?',
      confirmText: 'Yes, Submit Job',
      onConfirm: async () => {
        setSubmitting(true);
        setError('');

        try {
          await ticketsApi.submitJob(ticket.id);
          setAlertModal({
            isOpen: true,
            type: 'success',
            message: 'Job submitted successfully! The ticket has been moved to In Progress.'
          });
          onUpdate();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to submit job');
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  const handleSubmitPlanJob = () => {
    if (ticket.plan_job) {
      setAlertModal({
        isOpen: true,
        type: 'warning',
        message: 'This ticket already has a plan job associated with it.'
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      message: 'Submit a plan job for this ticket? Claude will create an implementation plan.',
      confirmText: 'Yes, Create Plan',
      onConfirm: async () => {
        setSubmitting(true);
        setError('');

        try {
          await ticketsApi.submitPlanJob(ticket.id);
          setAlertModal({
            isOpen: true,
            type: 'success',
            message: 'Plan job submitted successfully! The ticket has been moved to Planning.'
          });
          onUpdate();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to submit plan job');
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await ticketsApi.updateStatus(ticket.id, newStatus);
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleDeleteTicket = () => {
    setConfirmModal({
      isOpen: true,
      message: 'Are you sure you want to delete this ticket? This action cannot be undone.',
      confirmText: 'Yes, Delete Ticket',
      onConfirm: async () => {
        setSubmitting(true);
        setError('');

        try {
          await ticketsApi.delete(ticket.id);
          setAlertModal({
            isOpen: true,
            type: 'success',
            message: 'Ticket deleted successfully!'
          });
          // Close modal and refresh after a short delay
          setTimeout(() => {
            onClose();
            onUpdate();
          }, 1000);
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to delete ticket');
          setSubmitting(false);
        }
      }
    });
  };

  const handleSaveDescription = async () => {
    setSavingDescription(true);
    setError('');

    try {
      await ticketsApi.update(ticket.id, { description: editedDescription });
      setIsEditingDescription(false);
      setAlertModal({
        isOpen: true,
        type: 'success',
        message: 'Description updated successfully!'
      });
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update description');
    } finally {
      setSavingDescription(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedDescription(ticket.description || '');
    setIsEditingDescription(false);
  };

  // Check if current user can delete this ticket (creator or admin)
  const canDelete = user && (ticket.created_by.id === user.id || user.is_admin);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const handleImproveDescription = async () => {
    if (!ticket.description || !ticket.description.trim()) {
      setError('Please enter a description before improving it');
      return;
    }

    setImproving(true);
    setError('');

    try {
      const response = await ticketsApi.improveDescription(ticket.description);
      setImprovedDescription(response.data.improved_description);
      setShowPreview(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to improve description');
    } finally {
      setImproving(false);
    }
  };

  const handleAcceptImprovement = async () => {
    try {
      setSubmitting(true);
      await ticketsApi.update(ticket.id, { description: improvedDescription });
      setAlertModal({
        isOpen: true,
        type: 'success',
        message: 'Description updated successfully!'
      });
      setShowPreview(false);
      setImprovedDescription('');
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update description');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectImprovement = () => {
    setShowPreview(false);
    setImprovedDescription('');
  };

  return (
    <>
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Ticket #{ticket.id}: {ticket.title}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="ticket-modal-two-column">
          {/* LEFT COLUMN: Title, Description, Comments */}
          <div className="ticket-modal-left">
            <div className="ticket-detail-section">
              <label>Title</label>
              <h3 style={{ color: '#334155', fontSize: '20px', margin: '8px 0' }}>
                {ticket.title}
              </h3>
            </div>

            {/* Tabs Navigation */}
            <div className="ticket-tabs">
              <button
                className={`ticket-tab ${activeTab === 'description' ? 'active' : ''}`}
                onClick={() => setActiveTab('description')}
              >
                📝 Description
              </button>
              <button
                className={`ticket-tab ${activeTab === 'plan' ? 'active' : ''}`}
                onClick={() => setActiveTab('plan')}
                disabled={!plan}
              >
                📋 Plan {plan ? '✓' : ''}
              </button>
            </div>

            {/* Tab Content */}
            <div className="ticket-tab-content">
              {activeTab === 'description' && (
                <div className="ticket-detail-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label>Description</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {!isEditingDescription && ticket.description && (
                        <button
                          type="button"
                          onClick={handleImproveDescription}
                          disabled={improving || submitting}
                          className="btn-improve btn-sm"
                          style={{ padding: '4px 12px', fontSize: '13px' }}
                        >
                          {improving ? 'Improving...' : '✨ Improve with AI'}
                        </button>
                      )}
                      {!isEditingDescription && (
                        <button
                          onClick={() => setIsEditingDescription(true)}
                          className="btn-secondary btn-sm"
                          style={{ padding: '4px 12px', fontSize: '13px' }}
                        >
                          ✏️ Edit
                        </button>
                      )}
                    </div>
                  </div>
                  {isEditingDescription ? (
                    <div>
                      <textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        rows="8"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px',
                          lineHeight: '1.6',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          minHeight: '150px'
                        }}
                        placeholder="Provide detailed description of the work to be done..."
                        disabled={savingDescription}
                      />
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button
                          onClick={handleSaveDescription}
                          className="btn-modal-primary btn-sm"
                          disabled={savingDescription}
                        >
                          {savingDescription ? 'Saving...' : '💾 Save'}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="btn-secondary btn-sm"
                          disabled={savingDescription}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ticket-description-display">
                      {ticket.description || 'No description provided'}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'plan' && (
                <div className="ticket-detail-section">
                  <label>Implementation Plan</label>
                  {loadingPlan ? (
                    <div>Loading plan...</div>
                  ) : plan ? (
                    <div className="plan-display markdown-content">
                      <ReactMarkdown>{plan}</ReactMarkdown>
                    </div>
                  ) : (
                    <div>No plan available</div>
                  )}
                </div>
              )}
            </div>

            {/* Comments Section */}
            <div className="comments-section">
              <h4>💬 Comments ({comments.length})</h4>

              <div className="comments-list">
                {loadingComments ? (
                  <div className="empty-comments">Loading comments...</div>
                ) : comments.length === 0 ? (
                  <div className="empty-comments">No comments yet. Be the first to comment!</div>
                ) : (
                  comments.map((comment) => (
                    <div
                      key={comment.id}
                      className={`comment-item ${comment.is_system ? 'system-comment' : ''}`}
                    >
                      <div className="comment-header">
                        <span className={`comment-author ${comment.is_system ? 'system' : ''}`}>
                          {comment.is_system && '🤖 '}
                          {comment.author?.username || 'Unknown'}
                        </span>
                        <span className="comment-date">
                          {formatRelativeTime(comment.created_at)}
                        </span>
                      </div>
                      <div className="comment-content">{comment.content}</div>
                    </div>
                  ))
                )}
              </div>

              <form onSubmit={handleSubmitComment} className="comment-form">
                <textarea
                  className="comment-textarea"
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  disabled={submittingComment}
                />
                <button
                  type="submit"
                  className="comment-submit"
                  disabled={submittingComment || !newComment.trim()}
                >
                  {submittingComment ? 'Posting...' : 'Post Comment'}
                </button>
              </form>
            </div>
          </div>

          {/* RIGHT COLUMN: Controls and Metadata */}
          <div className="ticket-modal-right">
            <div className="ticket-detail-section">
              <label>Status</label>
              <select
                value={ticket.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="ticket-status-select"
                disabled={improving}
              >
                <option value="todo">To Do</option>
                <option value="planning">Planning</option>
                <option value="ready_to_implement">Ready to Implement</option>
                <option value="in_progress">In Progress</option>
                <option value="complete">Complete</option>
              </select>
            </div>

            <div className="ticket-detail-section">
              <label>Priority</label>
              <span className={`priority-badge priority-${ticket.priority}`}>
                {ticket.priority}
              </span>
            </div>

            <div className="ticket-detail-section">
              <label>Created By</label>
              <p>{ticket.created_by.username}</p>
            </div>

            {ticket.assignee && (
              <div className="ticket-detail-section">
                <label>Assigned To</label>
                <p>{ticket.assignee.username}</p>
              </div>
            )}

            <div className="ticket-detail-section">
              <label>Timestamps</label>
              <p style={{ fontSize: '14px', color: '#64748b' }}>
                Created: {formatDate(ticket.created_at)}<br />
                Updated: {formatDate(ticket.updated_at)}
              </p>
            </div>

            {ticket.job && (
              <div className="ticket-detail-section ticket-job-section">
                <label>Associated Job</label>
                <div className="job-info-card">
                  <div className="job-info-header">
                    <span className={`job-status status-${ticket.job.status}`}>
                      {ticket.job.status}
                    </span>
                  </div>
                  <p><strong>Branch:</strong> {ticket.job.branch_name}</p>
                  <p><strong>Command:</strong> {ticket.job.command}</p>
                  {ticket.job.pr_url && (
                    <a
                      href={ticket.job.pr_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-success btn-sm"
                    >
                      🔗 View Pull Request
                    </a>
                  )}
                  {ticket.job.error_message && (
                    <div className="alert alert-error" style={{ marginTop: '12px' }}>
                      {ticket.job.error_message}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Plan Job Display */}
            {ticket.plan_job && (
              <div className="ticket-detail-section ticket-job-section">
                <label>Plan Job</label>
                <div className="job-info-card">
                  <div className="job-info-header">
                    <span className={`job-status status-${ticket.plan_job.status}`}>
                      {ticket.plan_job.status}
                    </span>
                  </div>
                  <p><strong>Type:</strong> Plan Generation</p>
                  {ticket.plan_job.error_message && (
                    <div className="alert alert-error" style={{ marginTop: '12px' }}>
                      {ticket.plan_job.error_message}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginTop: 'auto', paddingTop: '20px' }}>
              {/* Show Create Plan button if: no plan exists, no plan_job running, and status is 'todo' */}
              {!plan && !ticket.plan_job && ticket.status === 'todo' && (
                <button
                  onClick={handleSubmitPlanJob}
                  className="btn-secondary"
                  style={{ width: '100%', marginBottom: '12px' }}
                  disabled={submitting || improving}
                >
                  {submitting ? '⏳ Submitting...' : '📋 Create Plan (Optional)'}
                </button>
              )}

              {/* Submit Job button - always available when no job exists */}
              {!ticket.job && (
                <button
                  onClick={handleSubmitJob}
                  className="btn-modal-primary"
                  style={{ width: '100%', marginBottom: '12px' }}
                  disabled={submitting || improving}
                >
                  {submitting ? '⏳ Submitting...' : plan ? '🔨 Submit Implementation Job' : '🔨 Submit Job'}
                </button>
              )}

              {canDelete && (
                <button
                  onClick={handleDeleteTicket}
                  className="btn-danger"
                  style={{ width: '100%', marginBottom: '12px' }}
                  disabled={submitting || improving}
                >
                  🗑️ Delete Ticket
                </button>
              )}
              <button onClick={onClose} className="btn-secondary" style={{ width: '100%' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        type={alertModal.type}
        message={alertModal.message}
      />

      <AlertModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type="confirm"
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        onConfirm={() => {
          setConfirmModal({ ...confirmModal, isOpen: false });
          if (confirmModal.onConfirm) confirmModal.onConfirm();
        }}
      />

    </div>

      {showPreview && (
        <ImproveDescriptionPreview
          improvedDescription={improvedDescription}
          onAccept={handleAcceptImprovement}
          onReject={handleRejectImprovement}
          accepting={submitting}
          acceptLabel="Accept & Update"
        />
      )}
    </>
  );
};

export default TicketModal;
